import { type ReactElement, type ReactNode, type SyntheticEvent, useMemo, useState } from 'react';

import { kebabCase } from '../../utils/stringHelpers';
import { TabBox, TabStyled, TabsList, TabsRoot } from './Tabs.styled';

export type TabIconPosition = 'top' | 'bottom' | 'start' | 'end';

export type TabItem = {
  /** Stable id used for keys and test ids. */
  id: string;
  /** Tab label. */
  label: string;
  /** Panel content. */
  content: ReactNode;
  /** When false, tab is omitted from the list. Defaults to true. */
  visible?: boolean;
  /** When true, tab is shown but not selectable. */
  disabled?: boolean;
  icon?: ReactElement;
  iconPosition?: TabIconPosition;
  overflow?: string;
};

export type TabsProps = {
  tabs: TabItem[];
  /** Prefix for aria/test ids. */
  idPrefix?: string;
  /** Controlled selected index among visible tabs. */
  value?: number;
  /** Uncontrolled initial index among visible tabs. */
  defaultValue?: number;
  onChange?: (index: number, tab: TabItem) => void;
  dataTestID?: string;
};

function TabPanel({
  children,
  value,
  index,
  idPrefix,
  overflow,
  dataTestID,
}: Readonly<{
  children?: ReactNode;
  value: number;
  index: number;
  idPrefix: string;
  overflow?: string;
  dataTestID?: string;
}>): ReactElement | null {
  if (value !== index) {
    return null;
  }

  return (
    <TabBox
      role="tabpanel"
      overflow={overflow}
      id={`${idPrefix}-tabpanel-${index}`}
      aria-labelledby={`${idPrefix}-tab-${index}`}
      data-testid={dataTestID}
    >
      {children}
    </TabBox>
  );
}

/**
 * Shared Tabs built on MUI Tabs.
 * @see https://mui.com/material-ui/react-tabs/
 */
export function Tabs({
  tabs,
  idPrefix = 'tabs',
  value: controlledValue,
  defaultValue = 0,
  onChange,
  dataTestID,
}: Readonly<TabsProps>): ReactElement | null {
  const visibleTabs = useMemo(() => tabs.filter((tab) => tab.visible !== false), [tabs]);

  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const rawValue = isControlled ? controlledValue : internalValue;

  const selectedValue = useMemo(() => {
    if (visibleTabs.length === 0) {
      return 0;
    }

    if (rawValue < 0 || rawValue >= visibleTabs.length) {
      return 0;
    }

    if (visibleTabs[rawValue]?.disabled) {
      const firstEnabled = visibleTabs.findIndex((tab) => !tab.disabled);
      return firstEnabled >= 0 ? firstEnabled : 0;
    }

    return rawValue;
  }, [rawValue, visibleTabs]);

  if (visibleTabs.length === 0) {
    return null;
  }

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue, visibleTabs[newValue]);
  };

  return (
    <TabsRoot data-testid={dataTestID ?? `${idPrefix}-tabs-root`}>
      <TabsList value={selectedValue} onChange={handleChange} data-testid={`${idPrefix}-tabs`}>
        {visibleTabs.map((tab, index) => (
          <TabStyled
            key={tab.id}
            label={tab.label}
            icon={tab.icon}
            iconPosition={tab.iconPosition}
            disabled={tab.disabled}
            id={`${idPrefix}-tab-${index}`}
            aria-controls={`${idPrefix}-tabpanel-${index}`}
            data-testid={`${kebabCase(tab.label)}-tab`}
          />
        ))}
      </TabsList>

      {visibleTabs.map((tab, index) => (
        <TabPanel
          key={`${tab.id}-panel`}
          value={selectedValue}
          index={index}
          idPrefix={idPrefix}
          overflow={tab.overflow}
          dataTestID={`${idPrefix}-tab-${kebabCase(tab.label)}`}
        >
          {tab.content}
        </TabPanel>
      ))}
    </TabsRoot>
  );
}

export default Tabs;
